import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useStandardRole } from "@/hooks/useStandardRole";

const DISMISS_KEY = "subscription-grace-banner-dismissed";

/**
 * Non-blocking banner shown while a tenant's subscription has lapsed but is still
 * inside its grace window (full access retained). Mirrors the grace logic in
 * public.is_tenant_access_enabled / src/lib/tenant-access.ts: a tenant is "in
 * grace" when access is still enabled, the status is 'expired', and the grace end
 * date is in the future. Dismissible for the session; returns on next sign-in.
 */
export const SubscriptionGraceBanner = () => {
  const navigate = useNavigate();
  const { data: standardRole } = useStandardRole();
  const [dismissed, setDismissed] = useState<boolean>(
    () => sessionStorage.getItem(DISMISS_KEY) === "1"
  );

  if (standardRole?.isSuperAdmin) return null;

  const access = standardRole?.tenantAccess;
  const graceEndsAt = access?.gracePeriodEndsAt;
  const inGrace =
    !!access?.isEnabled &&
    access.status === "expired" &&
    !!graceEndsAt &&
    new Date(graceEndsAt).getTime() > Date.now();

  if (!inGrace || dismissed) return null;

  const daysLeft = Math.max(
    1,
    Math.ceil((new Date(graceEndsAt!).getTime() - Date.now()) / (24 * 60 * 60 * 1000))
  );

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="mx-4 md:mx-6 mb-4 flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
      <AlertTriangle className="h-5 w-5 flex-shrink-0" />
      <p className="flex-1 text-sm">
        <span className="font-semibold">Your subscription has lapsed.</span> You have{" "}
        {daysLeft} day{daysLeft === 1 ? "" : "s"} left to renew before access is suspended.
      </p>
      <Button
        size="sm"
        onClick={() => navigate("/organization/subscription")}
        className="flex-shrink-0"
      >
        Renew now
      </Button>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={handleDismiss}
        className="flex-shrink-0 rounded p-1 transition-colors hover:bg-amber-100 dark:hover:bg-amber-500/20"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default SubscriptionGraceBanner;
